import ErrorMessage from '@/components/ErrorMessage';
import { BaseErrorModel, BaseResponseModel } from '@/models/core.model';
import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import { Drawer, DrawerProps, Button, Form, Schema, FormInstance, SelectPicker } from 'rsuite';
import {
  ClubMemberRoleAssignableList,
  ClubMemberRoleEnum,
  ClubMemberRoleList,
  ClubMembershipStatusEnum,
  ClubMembershipStatusList,
  RunningClubMember,
  UpsertRunningClubMember
} from '@/models/running-club-member.model';
import { upsertRunningClubMember } from '@/services/running-club-member.service';
import FetchingSelect from '@/components/FetchingSelect';
import { getRunningClubsLookup } from '@/services/running-club.service';
import { getUsersLookup } from '@/services/user.service';
import { RoleEnum } from '@/models/user.model';

const modelSchema = Schema.Model<UpsertRunningClubMember>({
  id: Schema.Types.NumberType(),
  userId: Schema.Types.NumberType().isRequired('This field is required.'),
  runningClubId: Schema.Types.NumberType().isRequired('This field is required.'),
  role: Schema.Types.NumberType().isRequired('This field is required.'),
  status: Schema.Types.NumberType().isRequired('This field is required.'),
});

interface RunningClubMemberDrawerProps extends DrawerProps{
  value?: RunningClubMember,
  userId?: number,
  runningClubId?: number,
};

const RunningClubMemberDrawer : React.FC<RunningClubMemberDrawerProps> = ({value, runningClubId, userId, open, onClose, ...rest}) => {
  // Both enums are [EnumDataType]-validated by the API; undefined binds to 0 and 400s.
  const emptyModel: UpsertRunningClubMember = {
    id: undefined,
    userId: userId,
    runningClubId: runningClubId,
    role: ClubMemberRoleEnum.Member,
    status: ClubMembershipStatusEnum.Active,
  };

  const [formValue, setFormValue] = useState<UpsertRunningClubMember>(emptyModel);
  const [formError, setFormError] = useState({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel|undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  useEffect(() => {
    setFormValue(value ?? emptyModel);
  }, [value, userId, runningClubId]);

  // An existing Owner row is shown but not editable here: demoting or re-assigning
  // ownership must go through the Ownership tab so the club's JoinType stays in sync.
  const isOwnerRow = formValue.role === ClubMemberRoleEnum.Owner;

  const handleDrawerClose = (event?: React.SyntheticEvent<Element, Event> | undefined) => {
    setFormValue(emptyModel);
    setErrorModel(undefined);
    setLoading(false);
    if(onClose){
      onClose(event);
    }
  };

  const handleSubmit = async event => {
    if (formRef.current && !formRef.current.check()) {
      console.log(formError);
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result: BaseResponseModel<RunningClubMember> = await upsertRunningClubMember(formValue);
      if (result.success) {
        handleDrawerClose(event);
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }
    finally{
      setLoading(false);
    }
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      ...value,
      // Owner can never be picked here, whatever the form reports.
      role: value.role === ClubMemberRoleEnum.Owner ? prevFormValue.role : value.role,
    }));
  };


  return (
    <Drawer backdrop="static" size="sm" placement="right"
      open={open}
      onClose={onClose}
      {...rest}
    >
      <Drawer.Header>
        <Drawer.Title>{value ? 'Edit a' : 'Add a new'} Running Club Member</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleSubmit} loading={loading} appearance="primary">
            Submit
          </Button>
          <Button onClick={handleDrawerClose} appearance="subtle">
            Cancel
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form name='upsert-running-club-member' fluid ref={formRef}
          onChange={handleFormChange}
          onCheck={setFormError}
          formValue={formValue}
          model={modelSchema}>
          <Form.Group>
            <Form.Label>Running Club</Form.Label>
            <Form.Control name="runningClubId" block
              accepter={FetchingSelect}
              fetchData={(text, page) => getRunningClubsLookup(text, page)}
              disabled={!!runningClubId}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>User</Form.Label>
            <Form.Control name="userId" block
              accepter={FetchingSelect}
              fetchData={(text, page) => getUsersLookup(text, RoleEnum.User, page)}
              disabled={!!userId}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Role</Form.Label>
            <Form.Control name="role" block
              accepter={SelectPicker}
              data={isOwnerRow ? ClubMemberRoleList : ClubMemberRoleAssignableList}
              searchable={false}
              cleanable={false}
              disabled={isOwnerRow}
            />
            <Form.HelpText>Ownership is assigned on the Ownership tab.</Form.HelpText>
          </Form.Group>
          <Form.Group>
            <Form.Label>Status</Form.Label>
            <Form.Control name="status" block
              accepter={SelectPicker}
              data={ClubMembershipStatusList}
              searchable={false}
              cleanable={false}
            />
          </Form.Group>
        </Form>
        {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}
      </Drawer.Body>
    </Drawer>
  );
};

export default RunningClubMemberDrawer;
